import type { MembershipCtaPlacement } from '@elara/client-core/states/SharedItineraryState';

import { cn } from '@elara/client-core/lib/utils';

import { useSharedItineraryState } from '#states/hooks.js';
import { buttonVariants, Logo } from '#ui';

function useMembershipLink(placement: MembershipCtaPlacement) {
  const sharedItineraryState = useSharedItineraryState();
  return {
    href: sharedItineraryState.membershipApplicationUrl(placement),
    onClick: () => sharedItineraryState.recordMembershipCtaClicked(placement),
  };
}

/**
 * Slim bar at the top of a shared itinerary — a quiet introduction for the friend or family member
 * viewing the trip. It is too early to ask them to apply, so it links to the marketing site to learn
 * more (same attribution and referral code); the application CTA is the closing card.
 */
export function MembershipBanner() {
  const sharedItineraryState = useSharedItineraryState();

  return (
    <div
      data-print="banner"
      className="w-full bg-camel-950 text-white text-sm"
      role="region"
      aria-label="Elara membership"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-center gap-3 px-4 md:px-8 py-2.5">
        <Logo variant="symbol" width={18} height={18} className="flex-shrink-0 text-white" />
        <p className="min-w-0 text-pretty text-white/80">This trip was planned with Elara.</p>
        <a
          href={sharedItineraryState.marketingSiteUrl('banner')}
          target="_blank"
          rel="noopener"
          onClick={() => sharedItineraryState.recordLearnMoreClicked('banner')}
          className="text-white underline underline-offset-4 whitespace-nowrap"
        >
          Learn more
        </a>
      </div>
    </div>
  );
}

/** Closing card at the end of a shared itinerary, inverted onto the banner's dark camel so it stands apart from the light itinerary above. */
export function MembershipCard() {
  const link = useMembershipLink('footer');
  const sharedItineraryState = useSharedItineraryState();

  return (
    <div
      data-print="membership-card"
      className="flex flex-col items-center gap-6 py-24 md:py-32 px-4 md:px-8 bg-camel-950 text-white text-center"
    >
      <span data-print="membership-logo" className="flex">
        <Logo variant="symbol" width={40} height={40} className="text-white" />
      </span>
      <div className="flex flex-col gap-3 max-w-prose">
        <h3 className="text-2xl md:text-3xl font-display font-light text-pretty">
          Luxury travel, evolved for the modern family
        </h3>
        <p className="text-white/70 text-pretty">
          Elara combines a proprietary travel engine with expert human advisors, turning endless possibilities into the
          perfect family trip.
        </p>
      </div>
      {/* Paper has no buttons: the printed page names the site instead. */}
      <p className="hidden print:block text-sm">Learn more at elara.travel</p>
      <div data-print="membership-actions" className="flex flex-wrap justify-center gap-3">
        <a
          href={link.href}
          target="_blank"
          rel="noopener"
          onClick={link.onClick}
          // Light button on the dark card; the default/accent fills are dark camel and vanish here.
          className={cn(
            buttonVariants({ size: 'lg' }),
            'bg-white text-camel-950 hover:bg-camel-100 hover:text-camel-950',
          )}
        >
          Apply for Membership
        </a>
        <a
          href={sharedItineraryState.marketingSiteUrl('footer')}
          target="_blank"
          rel="noopener"
          onClick={() => sharedItineraryState.recordLearnMoreClicked('footer')}
          className={cn(
            buttonVariants({ variant: 'outline', size: 'lg' }),
            'border-white/40 text-white hover:bg-white/10 hover:text-white hover:border-white/60',
          )}
        >
          Learn more
        </a>
      </div>
    </div>
  );
}
